'use client';

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';

import { DramaMallApi } from '@/apis/drama-mall';
import { type IDrama, EModuleType } from '@/apis/drama-mall';
import EmptyPage, { EEmptyPageType } from '@/components/empty-page';
import { SESSION_KEY_DISCOVER_CACHE } from '@/constants/storage';
import { sessionStore } from '@/utils/storage';

import DefaultHeader from './components/default-header';
import DiscoverContent from './components/discover-content';
import Searing from './components/searing';

import './index.scss';

interface IDiscoverCache {
    topPickList: IDrama[];
    mostPopularList: IDrama[];
    forYouList: IDrama[];
    hasMore: boolean;
    cursor: string;
    /** 滚动容器位置 Tab 切换返回时恢复 */
    scrollTop: number;
}

/** 读取发现页 sessionStorage 缓存 解析失败 / 不存在 / 数据全空 返回 null */
function readDiscoverCache(): IDiscoverCache | null {
    const raw = sessionStore.getItem(SESSION_KEY_DISCOVER_CACHE);
    if (!raw) {
        return null;
    }
    try {
        const cache = JSON.parse(raw) as IDiscoverCache;
        // 3 个列表仅有一个空视为无效缓存 让外部走 fetch 路径
        if (!cache.topPickList?.length || !cache.mostPopularList?.length || !cache.forYouList?.length) {
            return null;
        }
        return cache;
    } catch {
        return null;
    }
}

/** 写入发现页 sessionStorage 缓存 */
function writeDiscoverCache(cache: IDiscoverCache): void {
    sessionStore.setItem(SESSION_KEY_DISCOVER_CACHE, JSON.stringify(cache));
}

export default function HomePage() {
    const tCommon = useTranslations('Common');
    const [isSearching, setIsSearching] = useState(false);
    const [isError, setIsError] = useState(false);

    // 仅在 mount 时读一次缓存 用于初始化下方 5 个 state
    const [initialCache] = useState(readDiscoverCache);

    // 列表 + 分页相关 优先使用 sessionStorage 缓存 Tab 切换返回时秒开
    const [hasMore, setHasMore] = useState(initialCache?.hasMore ?? false);
    const [topPickList, setTopPickList] = useState<IDrama[]>(initialCache?.topPickList ?? []);
    const [mostPopularList, setMostPopularList] = useState<IDrama[]>(initialCache?.mostPopularList ?? []);
    const [forYouList, setForYouList] = useState<IDrama[]>(initialCache?.forYouList ?? []);
    const [cursor, setCursor] = useState(initialCache?.cursor ?? '');

    // 是否已成功加载过一次 缓存命中视为已初始化，避免触发空结果错误页
    const [isInitial, setIsInitial] = useState(initialCache !== null);

    // 滚动容器 DOM 引用 用于读写 scrollTop
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // 实时记录最新 scrollTop，持久化时取 ref 避免每次滚动写 storage
    const lastScrollTopRef = useRef(initialCache?.scrollTop ?? 0);

    const handleFocus = () => {
        setIsSearching(true);
    };
    const handleBack = () => {
        setIsSearching(false);
    };

    const getHomeData = async () => {
        const { data } = await DramaMallApi.getDramaMallHome({
            refresh_type: cursor === '' ? 1 : 2,
            module_type: 1,
            cursor,
            page_size: 20,
        });
        if (!data) {
            return;
        }
        setIsError(false);
        const { modules } = data;
        if (!isInitial && modules.length === 0) {
            setIsError(true);
            return;
        }
        if (modules.length === 0) {
            setCursor('');
            setHasMore(false);
        }
        setIsInitial(true);
        modules.forEach(module => {
            if (module.type === EModuleType.Hot) {
                setTopPickList(module.dramas);
            } else if (module.type === EModuleType.New) {
                setMostPopularList(module.dramas);
            } else if (module.type === EModuleType.Feed) {
                setForYouList(prev => [...prev, ...module.dramas]);
                setCursor(module?.cursor || '');
                setHasMore(module?.has_next || false);
            }
        });
    };

    // 首次加载 有缓存则跳过 fetch 直接渲染缓存内容
    useEffect(() => {
        if (initialCache) {
            return;
        }
        try {
            getHomeData();
        } catch (error) {
            console.error('discover page getHomeData error', error);
            setIsError(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // mount 时恢复滚动位置并注册 scroll 监听，记录 scrollTop 供 Tab 切回复用
    useLayoutEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) {
            return undefined;
        }

        const top = initialCache?.scrollTop;
        if (top) {
            el.scrollTop = top;
            lastScrollTopRef.current = top;
        }

        const onScroll = () => {
            lastScrollTopRef.current = el.scrollTop;
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
        };
        // 仅 mount 时恢复位置并绑定监听
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上
    }, []);

    // 列表/分页变化时回写缓存 卸载时再写一次 兜底「滚动后未触发任何 state 变化」场景
    useEffect(() => {
        const persist = () =>
            writeDiscoverCache({
                topPickList,
                mostPopularList,
                forYouList,
                hasMore,
                cursor,
                scrollTop: lastScrollTopRef.current,
            });
        persist();
        return persist;
    }, [topPickList, mostPopularList, forYouList, hasMore, cursor]);

    if (isError) {
        return (
            <EmptyPage
                tips={tCommon('network_error')}
                btnInfo={tCommon('try_again')}
                onClick={getHomeData}
                type={EEmptyPageType.ERROR}
            />
        );
    }
    return (
        <div ref={scrollContainerRef} className='discover-page relative size-full overflow-auto bg-white'>
            {isSearching ? (
                <Searing onBack={handleBack} />
            ) : (
                <>
                    <DefaultHeader onFocus={handleFocus} />
                    <DiscoverContent
                        topPickList={topPickList}
                        mostPopularList={mostPopularList}
                        forYouList={forYouList}
                        hasMore={hasMore}
                        loadMore={getHomeData}
                    />
                </>
            )}
        </div>
    );
}
